import { create } from 'zustand';
import { lessonsApi } from '../api/lessons';
import { userPreferencesApi } from '../api/userPreferences';

// Load tile size from localStorage
const loadTileSize = () => {
  try {
    const saved = localStorage.getItem('sb-tile-size');
    return saved ? parseInt(saved) : 72;
  } catch {
    return 72;
  }
};

const loadShowTilePinyin = () => {
  try {
    return localStorage.getItem('sb-show-tile-pinyin') === 'true';
  } catch {
    return false;
  }
};

const loadShowTileEnglish = () => {
  try {
    return localStorage.getItem('sb-show-tile-english') === 'true';
  } catch {
    return false;
  }
};

const loadRandomMode = () => {
  try {
    return localStorage.getItem('sb-random-mode') === 'true';
  } catch {
    return false;
  }
};

// Best-effort sync: write the change to the remote user-preferences API
// without blocking the UI. Errors are swallowed because localStorage is the
// source of truth on the client.
const syncPreferenceToServer = (key, value) => {
  userPreferencesApi
    .patch({ [key]: value })
    .catch((err) => {
      console.debug('user-preferences PATCH failed:', err.message);
    });
};

export const useGameStore = create((set, get) => ({
  // Data
  lessons: [],
  currentLessonIndex: 0,
  currentSentenceIndex: 0,
  lessonLoading: false,
  lessonLoadError: null,
  
  // Game state
  slotContents: [],
  selectedTile: null,
  feedback: { type: null, message: '' },
  typedAnswer: '',
  typedFeedback: null, // null | 'correct' | 'wrong'
  tilesFeedback: null, // null | 'correct' | 'wrong'
  showPinyin: false,
  
  // UI state
  tileSize: loadTileSize(),
  showTilePinyin: loadShowTilePinyin(),
  showTileEnglish: loadShowTileEnglish(),
  randomMode: loadRandomMode(),
  shuffleNonce: 0,
  settingsOpen: false,
  strokeModalOpen: false,
  currentStrokeChar: null,
  
  // Actions
  setLessons: (lessons) => set({ lessons }),
  
  // Replace a single lesson (e.g. after fetching its full content or in-place edit)
  updateLesson: (index, newLesson) => {
    const { lessons } = get();
    const next = [...lessons];
    next[index] = newLesson;
    set({ lessons: next });
  },
  
  setCurrentLesson: async (index) => {
    const { lessons } = get();
    if (!lessons[index]) return;
    
    set({ 
      currentLessonIndex: index,
      currentSentenceIndex: 0,
      slotContents: [],
      selectedTile: null,
      feedback: { type: null, message: '' },
      typedAnswer: '',
      typedFeedback: null,
      tilesFeedback: null,
      showPinyin: false,
      lessonLoadError: null,
    });
    
    let lesson = lessons[index];
    
    // Lazy-load full lesson content if dialogue isn't loaded yet
    if (!lesson.dialogue || lesson.dialogue.length === 0) {
      set({ lessonLoading: true });
      try {
        const full = await lessonsApi.get(lesson.code);
        const merged = { ...lesson, ...full };
        const next = [...get().lessons];
        next[index] = merged;
        set({ lessons: next, lessonLoading: false });
        lesson = merged;
      } catch (err) {
        console.error('Failed to load lesson:', err);
        set({ lessonLoading: false, lessonLoadError: err.message });
        return;
      }
    }
    
    if (lesson?.dialogue?.[0]?.breakdown) {
      set({ 
        slotContents: new Array(lesson.dialogue[0].breakdown.length).fill(null) 
      });
    }
  },
  
  // Pick a random sentence index in the current lesson (different from current
  // when possible). Used when randomMode is on.
  _pickRandomSentenceIndex: () => {
    const { lessons, currentLessonIndex, currentSentenceIndex } = get();
    const lesson = lessons[currentLessonIndex];
    const total = lesson?.dialogue?.length || 0;
    if (total <= 1) return 0;
    let next = Math.floor(Math.random() * total);
    if (next === currentSentenceIndex) next = (next + 1) % total;
    return next;
  },

  nextSentence: () => {
    const { currentSentenceIndex, lessons, currentLessonIndex, randomMode, _pickRandomSentenceIndex } = get();
    const lesson = lessons[currentLessonIndex];
    const nextIndex = randomMode
      ? _pickRandomSentenceIndex()
      : (currentSentenceIndex + 1) % lesson.dialogue.length;
    
    set({
      currentSentenceIndex: nextIndex,
      slotContents: new Array(lesson.dialogue[nextIndex].breakdown.length).fill(null),
      selectedTile: null,
      feedback: { type: null, message: '' },
      typedAnswer: '',
      typedFeedback: null,
      tilesFeedback: null,
      showPinyin: false
    });
  },
  
  prevSentence: () => {
    const { currentSentenceIndex, lessons, currentLessonIndex, randomMode, _pickRandomSentenceIndex } = get();
    const lesson = lessons[currentLessonIndex];
    const prevIndex = randomMode
      ? _pickRandomSentenceIndex()
      : (currentSentenceIndex - 1 + lesson.dialogue.length) % lesson.dialogue.length;
    
    set({
      currentSentenceIndex: prevIndex,
      slotContents: new Array(lesson.dialogue[prevIndex].breakdown.length).fill(null),
      selectedTile: null,
      feedback: { type: null, message: '' },
      typedAnswer: '',
      typedFeedback: null,
      tilesFeedback: null,
      showPinyin: false
    });
  },
  
  setTypedAnswer: (value) => set({ typedAnswer: value, typedFeedback: null }),
  
  // Tile actions
  selectTile: (tile, from, slotIndex) => {
    set({ selectedTile: { tile, from, slotIndex } });
  },
  
  clearSelection: () => set({ selectedTile: null }),
  
  placeTile: (targetSlotIndex) => {
    const { selectedTile, slotContents } = get();
    if (!selectedTile) return;
    
    const { tile, from, slotIndex: fromSlot } = selectedTile;
    const newSlots = [...slotContents];
    
    // Remove from previous slot if coming from one
    if (from === 'slot' && fromSlot !== undefined) {
      newSlots[fromSlot] = null;
    }
    
    // Place in target (overwrites if occupied)
    newSlots[targetSlotIndex] = tile;
    
    set({ slotContents: newSlots, selectedTile: null });
  },
  
  returnTileToPool: () => {
    const { selectedTile, slotContents } = get();
    if (!selectedTile || selectedTile.from !== 'slot') return;
    
    const newSlots = [...slotContents];
    newSlots[selectedTile.slotIndex] = null;
    
    set({ slotContents: newSlots, selectedTile: null });
  },
  
  resetCurrent: () => {
    const { lessons, currentLessonIndex, currentSentenceIndex } = get();
    const lesson = lessons[currentLessonIndex];
    const sentence = lesson.dialogue[currentSentenceIndex];
    
    set({
      slotContents: new Array(sentence.breakdown.length).fill(null),
      selectedTile: null,
      feedback: { type: null, message: '' },
      typedAnswer: '',
      typedFeedback: null,
      tilesFeedback: null,
      showPinyin: false
    });
  },
  
  shufflePool: () => {
    // Bump the nonce so TilePool's useMemo recomputes the shuffle order.
    set(state => ({ shuffleNonce: state.shuffleNonce + 1, selectedTile: null }));
  },
  
  checkAnswer: () => {
    const { lessons, currentLessonIndex, currentSentenceIndex, slotContents, typedAnswer } = get();
    const lesson = lessons[currentLessonIndex];
    const sentence = lesson.dialogue[currentSentenceIndex];
    const correct = sentence.breakdown.map(b => b.char).join('');
    const built = slotContents.map(t => t ? t.char : '').join('');
    
    const normalize = (s) => (s || '').replace(/\s+/g, '').trim();
    const stripPunct = (s) => s.replace(/[，。！？、；："'（）,.!?;:"'()]/g, '');
    
    const typed = normalize(typedAnswer);
    const fullCorrect = normalize(sentence.chinese);
    const correctChars = normalize(correct);
    
    const hasTiles = built.length > 0;
    const hasTyped = typed.length > 0;
    
    if (!hasTiles && !hasTyped) {
      set({ feedback: { type: 'error', message: 'Place characters or type the answer!' } });
      return;
    }
    
    // Evaluate tiles (if attempted)
    let tilesCorrect = null;
    if (hasTiles) {
      tilesCorrect = (built.length >= correct.length) && (built === correct);
    }
    
    // Evaluate typed answer (if attempted)
    let typedCorrect = null;
    if (hasTyped) {
      typedCorrect = (typed === fullCorrect) ||
                     (typed === correctChars) ||
                     (stripPunct(typed) === stripPunct(correctChars));
    }
    
    const anyCorrect = tilesCorrect === true || typedCorrect === true;
    
    set({
      tilesFeedback: tilesCorrect === null ? null : (tilesCorrect ? 'correct' : 'wrong'),
      typedFeedback: typedCorrect === null ? null : (typedCorrect ? 'correct' : 'wrong'),
      feedback: anyCorrect 
        ? { type: 'success', message: '✅ Correct!' }
        : { type: 'error', message: '❌ Not quite — try again!' },
      showPinyin: anyCorrect
    });
  },
  
  // Settings
  setTileSize: (size) => {
    set({ tileSize: size });
    try {
      localStorage.setItem('sb-tile-size', size);
    } catch {}
  },

  setShowTilePinyin: (value) => {
    set({ showTilePinyin: value });
    try {
      localStorage.setItem('sb-show-tile-pinyin', String(value));
    } catch {}
    syncPreferenceToServer('podShowTilePinyin', value);
  },

  setShowTileEnglish: (value) => {
    set({ showTileEnglish: value });
    try {
      localStorage.setItem('sb-show-tile-english', String(value));
    } catch {}
    syncPreferenceToServer('podShowTileEnglish', value);
  },

  setRandomMode: (value) => {
    set({ randomMode: value });
    try {
      localStorage.setItem('sb-random-mode', String(value));
    } catch {}
    syncPreferenceToServer('podRandomMode', value);
  },

  // Pull saved preferences from the server and merge them into local state.
  // Called on app boot (when authenticated) and after login.
  hydrateServerPreferences: async () => {
    try {
      const prefs = await userPreferencesApi.get();
      const updates = {};
      if (typeof prefs.podShowTilePinyin === 'boolean') {
        updates.showTilePinyin = prefs.podShowTilePinyin;
        try { localStorage.setItem('sb-show-tile-pinyin', String(prefs.podShowTilePinyin)); } catch {}
      }
      if (typeof prefs.podShowTileEnglish === 'boolean') {
        updates.showTileEnglish = prefs.podShowTileEnglish;
        try { localStorage.setItem('sb-show-tile-english', String(prefs.podShowTileEnglish)); } catch {}
      }
      if (typeof prefs.podRandomMode === 'boolean') {
        updates.randomMode = prefs.podRandomMode;
        try { localStorage.setItem('sb-random-mode', String(prefs.podRandomMode)); } catch {}
      }
      if (Object.keys(updates).length) set(updates);
    } catch (err) {
      console.debug('Could not hydrate server prefs:', err.message);
    }
  },
  
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),
  
  // Stroke modal
  openStrokeModal: (char) => set({ strokeModalOpen: true, currentStrokeChar: char }),
  closeStrokeModal: () => set({ strokeModalOpen: false, currentStrokeChar: null }),

  // Update a single character's info in the current sentence's breakdown.
  // Returns the updated lesson and sentence (or null if out of range).
  updateBreakdownChar: (sentenceIndex, charIndex, fields) => {
    const { lessons, currentLessonIndex } = get();
    const lesson = lessons[currentLessonIndex];
    const sentence = lesson?.dialogue?.[sentenceIndex];
    const existing = sentence?.breakdown?.[charIndex];
    if (!existing) return null;

    const updatedChar = { ...existing, ...fields };
    const updatedBreakdown = sentence.breakdown.map((b, i) => (i === charIndex ? updatedChar : b));
    const updatedSentence = { ...sentence, breakdown: updatedBreakdown };
    const updatedDialogue = lesson.dialogue.map((d, i) => (i === sentenceIndex ? updatedSentence : d));
    const updatedLesson = { ...lesson, dialogue: updatedDialogue };

    const nextLessons = [...lessons];
    nextLessons[currentLessonIndex] = updatedLesson;
    set({ lessons: nextLessons });

    return { lesson: updatedLesson, sentence: updatedSentence };
  },
}));

// Derived-state selectors (use these in components instead of store getters,
// which get frozen by Zustand's shallow merge on set())
export const selectCurrentLesson = (state) =>
  state.lessons[state.currentLessonIndex];

export const selectCurrentSentence = (state) =>
  state.lessons[state.currentLessonIndex]?.dialogue?.[state.currentSentenceIndex];

export const selectCharsInPool = (state) => {
  const sentence = selectCurrentSentence(state);
  if (!sentence) return [];
  
  const all = sentence.breakdown.map((b, i) => ({ ...b, origIndex: i }));
  const placed = new Set();
  state.slotContents.forEach(t => { if (t) placed.add(t.origIndex); });
  return all.filter(t => !placed.has(t.origIndex));
};
